import { addMonths, endOfDay, startOfDay } from "date-fns";
import type { FunctionComponent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { scmpPlus } from "scmp-sdk";

import { data as appData } from "shared/data";

import { Spacer } from "scmp-app/components/common/spacer";
import { DefinedDateRange } from "scmp-app/components/plus/date-picker/consts";
import type { DateRangePair } from "scmp-app/components/plus/date-picker/types";
import { useNewsAgendaContext } from "scmp-app/components/plus/news-agenda/contexts";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import type { filterAgendaFilterContent$key } from "scmp-app/queries/__generated__/filterAgendaFilterContent.graphql";
import type { filterAgendaFilterQuery } from "scmp-app/queries/__generated__/filterAgendaFilterQuery.graphql";
import type { hooksNewsAgendaFilter$data } from "scmp-app/queries/__generated__/hooksNewsAgendaFilter.graphql";

import { useNewsAgendaFilter } from "./hooks";
import {
  Container,
  StyledCheckbox,
  StyledDatePicker,
  StyledFormControlLabel,
  StyledLabel,
} from "./styles";

type ChildProps = {
  firstWithDetailsKeyEventAgenda: number;
  hasNextPage: boolean;
  infiniteScrollTriggerReference: (dom: HTMLElement | null) => void;
  selected: null | string;
  topics: hooksNewsAgendaFilter$data;
};

type Props = {
  children?: (props: ChildProps) => ReactNode;
  className?: string;
  entityId?: string | undefined;
  isDefaultCheckedKeyEvents?: boolean;
  startDate?: Date | undefined;
  withDatePicker?: boolean;
  withKeyEvent?: boolean;
};

const agendaPageSize = 25;

export const AgendaFilter: FunctionComponent<Props> = ({
  children,
  className,
  entityId,
  isDefaultCheckedKeyEvents = false,
  startDate,
  withDatePicker = true,
  withKeyEvent = true,
}) => {
  const [onlyKeyEvents, setOnlyKeyEvents] = useState(isDefaultCheckedKeyEvents);
  const startDateValue = startOfDay(startDate ?? new Date());
  const endDateValue = endOfDay(addMonths(startDateValue, 1));
  const defaultStartDate = useRef(startDateValue);
  const defaultEndDate = useRef(endDateValue);
  const [dateRange, setDateRange] = useState<DateRangePair>([
    defaultStartDate.current,
    defaultEndDate.current,
  ]);

  const data = useLazyLoadQuery<filterAgendaFilterQuery>(
    graphql`
      query filterAgendaFilterQuery(
        $applicationId: String!
        $startDate: Timestamp!
        $endDate: Timestamp!
        $types: [String]!
        $newsAgendaOptions: [String!]
      ) {
        ...filterAgendaFilterContent
          @arguments(
            applicationId: $applicationId
            startDate: $startDate
            endDate: $endDate
            types: $types
            newsAgendaOptions: $newsAgendaOptions
          )
      }
    `,
    {
      applicationId: appData.application.scmp.entityId,
      endDate: dateRange?.[1]?.getTime() ?? defaultEndDate.current.getTime(),
      newsAgendaOptions: onlyKeyEvents ? ["key_event"] : [],
      startDate: dateRange?.[0]?.getTime() ?? defaultStartDate.current.getTime(),
      types: [scmpPlus.topicType.newsAgenda],
    },
    {
      fetchPolicy: "store-and-network",
    },
  );

  const {
    data: topicList,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<filterAgendaFilterQuery, filterAgendaFilterContent$key>(
    graphql`
      fragment filterAgendaFilterContent on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 25 }
        applicationId: { type: "String!" }
        startDate: { type: "Timestamp!" }
        endDate: { type: "Timestamp!" }
        types: { type: "[String]!" }
        newsAgendaOptions: { type: "[String!]" }
      )
      @refetchable(queryName: "filterAgendaFilterPaginationQuery") {
        topics(
          after: $after
          first: $count
          applicationId: $applicationId
          filter: {
            types: $types
            newsAgendaDateRange: { startDate: $startDate, endDate: $endDate }
            newsAgendaOptions: $newsAgendaOptions
          }
          orderBy: { field: NEWS_AGENDA_START_DATE, direction: ASC }
        ) @connection(key: "agendaFilter__topics") {
          edges {
            ...hooksNewsAgendaFilter
          }
        }
      }
    `,
    data,
  );

  const { filtered, selected } = useNewsAgendaFilter(topicList.topics.edges, entityId);
  const { setCurrentAgendaId, setShowEmptyAgenda } = useNewsAgendaContext();
  useEffect(() => {
    if (selected) {
      setShowEmptyAgenda?.(false);
      setCurrentAgendaId?.(selected);
    } else {
      setShowEmptyAgenda?.(true);
      setCurrentAgendaId?.("");
    }
  }, [selected, setShowEmptyAgenda, setCurrentAgendaId]);

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(agendaPageSize),
  });

  const firstWithDetailsKeyEventAgenda = useMemo(
    () =>
      filtered.findIndex(
        ({ node }) =>
          !node.newsAgenda?.newsAgendaOptions.includes("disable_detail_view") &&
          node.newsAgenda?.newsAgendaOptions.includes("key_event"),
      ),
    [filtered],
  );

  return (
    <Container className={className}>
      <StyledDatePicker
        $isShow={withDatePicker}
        dateRange={dateRange}
        defaultDateRange={[defaultStartDate.current, defaultEndDate.current]}
        definedDateRanges={[
          DefinedDateRange.THIS_MONTH,
          DefinedDateRange.NEXT_3_MONTHS,
          DefinedDateRange.NEXT_6_MONTHS,
          DefinedDateRange.LAST_3_MONTHS,
          DefinedDateRange.LAST_6_MONTHS,
        ]}
        onChange={range => setDateRange(range)}
      />
      <Spacer />
      <StyledFormControlLabel
        $isShow={withKeyEvent}
        control={
          <StyledCheckbox
            checked={onlyKeyEvents}
            disableRipple
            onChange={event => setOnlyKeyEvents(event.target.checked)}
          />
        }
        label={<StyledLabel>Key events only</StyledLabel>}
      />
      {children?.({
        firstWithDetailsKeyEventAgenda,
        hasNextPage,
        infiniteScrollTriggerReference,
        selected,
        topics: filtered,
      })}
    </Container>
  );
};

AgendaFilter.displayName = "AgendaFilter";
